// NGX Academy content. Tags: MVP (implemented/simulated), FUTURE (planned), CONCEPT (research).
// Lesson bodies are authored in English; UI chrome is localized via lib/i18n.js.

export const LEVELS = [
  { id: 1, name: "Foundations" },
  { id: 2, name: "Operator" },
  { id: 3, name: "NGX Architecture" },
  { id: 4, name: "Industrial Integration" },
  { id: 5, name: "Partner / Technical Discussion" },
];

export const PATHS = [
  {
    id: "p1", title: "Industrial Foundations", level: 1, icon: "factory",
    desc: "The building blocks of automated production.",
    lessons: [
      {
        id: "automation", title: "What is Industrial Automation?", level: 1, tag: "MVP",
        objective: "Understand what automation is and the parts that make a production line run without constant human action.",
        simple: "Automation means machines performing tasks on their own, following rules, so people don't have to do every repetitive step by hand.",
        technical: "An automated cell combines sensors (gather data), controllers (decide), and actuators (act). Machines run programmed logic on production lines to move, process and assemble parts reliably and repeatably.",
        example: "A bottling line: a sensor detects a bottle, a controller triggers a valve, an actuator fills it, and a conveyor moves it on — thousands of times per hour.",
        ngx: "NGX sits above this layer. It does not replace the machines; it adds a human supervision layer for the moments where a decision or authorization is needed.",
        flow: ["Sensor", "Controller", "Actuator", "Production Line"],
        quiz: [
          { q: "Which three roles form the core of an automated cell?", options: ["Sensor, Controller, Actuator", "Screen, Mouse, Keyboard", "Camera, Cable, Cloud"], answer: 0, explain: "Sensors gather data, controllers decide, actuators act." },
          { q: "What does NGX add on top of an automated line?", options: ["A faster motor", "A human supervision/decision layer", "A new database engine"], answer: 1, explain: "NGX is a Human-in-the-Loop supervision layer, not a machine replacement." },
        ],
        takeaways: ["Automation = machines executing programmed tasks.", "Core loop: sense → decide → act.", "NGX supervises; it does not replace the machinery."],
      },
      {
        id: "plc", title: "What is a PLC?", level: 1, tag: "FUTURE",
        objective: "Understand the industrial computer that controls machines and how it could connect to NGX.",
        simple: "A PLC (Programmable Logic Controller) is a rugged industrial computer that reads inputs (buttons, sensors) and switches outputs (motors, valves) using simple, reliable control logic.",
        technical: "PLCs run deterministic scan cycles: read inputs → execute logic → update outputs. They are built for harsh environments, real-time response and decades of uptime — unlike a general-purpose PC.",
        example: "A PLC watches a proximity sensor; when a pallet arrives it stops the conveyor and signals a robot to pick.",
        ngx: "A future PLC Connector would expose PLC data/commands to NGX Core so operators can supervise PLC-driven processes.",
        flow: ["PLC", "Connector", "NGX Core", "Human Control Station"],
        quiz: [
          { q: "What is a defining trait of a PLC vs a normal computer?", options: ["It runs video games", "Deterministic, rugged real-time control", "It needs the cloud to work"], answer: 1, explain: "PLCs are deterministic and built for industrial reliability." },
          { q: "In NGX, how would a PLC reach the Human Control Station?", options: ["Directly with no layer", "Through a Connector into NGX Core", "By email"], answer: 1, explain: "PLC → Connector → NGX Core → Human Control Station." },
        ],
        takeaways: ["PLC = rugged deterministic industrial controller.", "Scan cycle: inputs → logic → outputs.", "A PLC Connector is a FUTURE NGX integration."],
      },
      {
        id: "hmi", title: "What is an HMI?", level: 1, tag: "MVP",
        objective: "Distinguish a traditional HMI from the NGX Human Control Station.",
        simple: "An HMI (Human-Machine Interface) is the screen operators use to see machine status and press buttons. NGX is more than that — it adds decision-making and audit.",
        technical: "A classic HMI mirrors machine state and offers manual controls for one machine/PLC. NGX is a vendor-agnostic decision and supervision layer: it captures AI proposals, requests human decisions, and records an auditable trail across many systems.",
        example: "An HMI shows 'Conveyor: ON'. NGX instead asks: 'AI is 61% sure this is Component A — Approve, Correct, Reject or Stop?' and logs the outcome.",
        ngx: "Do not present NGX as just another HMI. NGX orchestrates human decisions and knowledge capture, not only machine visualization.",
        quiz: [
          { q: "The key difference of NGX vs a traditional HMI?", options: ["Bigger buttons", "Human decision + audit across many systems", "It only shows temperature"], answer: 1, explain: "NGX adds Human-in-the-Loop decisions and an audit trail, vendor-agnostic." },
        ],
        takeaways: ["HMI = machine visualization + manual control.", "NGX = decision + supervision + audit layer.", "NGX is not simply an HMI."],
      },
      {
        id: "sensors-actuators", title: "Sensors and Actuators", level: 1, tag: "MVP",
        objective: "Understand how information becomes physical action.",
        simple: "Sensors sense the world, controllers decide, actuators move things.",
        technical: "Sensor → Information; Controller → Decision; Actuator → Physical Action. Vision, proximity and temperature are common sensors; motors, grippers and conveyors are common actuators.",
        example: "A camera (sensor) detects a part → the controller decides 'pick' → the gripper (actuator) closes.",
        ngx: "In the NGX simulation, the 'vision' sensor produces an AI classification and confidence that NGX evaluates before an actuator would act.",
        flow: ["Sensor → Information", "Controller → Decision", "Actuator → Physical Action"],
        quiz: [
          { q: "A gripper is an example of a...", options: ["Sensor", "Actuator", "Database"], answer: 1, explain: "A gripper performs a physical action → actuator." },
          { q: "A machine-vision camera is a...", options: ["Actuator", "Sensor", "Controller"], answer: 1, explain: "It gathers information → sensor." },
        ],
        takeaways: ["Sensor → Controller → Actuator.", "Vision/proximity/temperature = sensors.", "Motor/gripper/conveyor = actuators."],
      },
    ],
  },
  {
    id: "p2", title: "Robotics", level: 2, icon: "bot",
    desc: "Robotic arms, cobots and how they integrate.",
    lessons: [
      {
        id: "industrial-robots", title: "Industrial Robots", level: 2, tag: "FUTURE",
        objective: "Learn the anatomy and key terms of an industrial robot.",
        simple: "An industrial robot is a programmable mechanical arm with joints and a 'hand' (end effector) that moves parts precisely.",
        technical: "Key terms: joints (axes), end effector/gripper, payload (max weight), reach (working envelope), robot controller (the brain), and safety systems (guards, e-stops).",
        example: "A 6-axis arm with a 5 kg payload and 900 mm reach picks parts from a tray and places them in a fixture.",
        ngx: "A FUTURE Robot Connector would let NGX supervise a real arm. The current MVP only simulates this.",
        quiz: [
          { q: "'Reach' of a robot refers to...", options: ["Its internet speed", "Its working envelope/distance", "Its color"], answer: 1, explain: "Reach is the distance/volume the arm can operate within." },
          { q: "The 'end effector' is...", options: ["The power cable", "The tool/gripper at the arm tip", "The floor mount"], answer: 1, explain: "The end effector is the tool at the end of the arm." },
        ],
        takeaways: ["Joints, end effector, payload, reach, controller, safety.", "Robot control is a FUTURE NGX integration."],
      },
      {
        id: "cobots", title: "Cobots", level: 2, tag: "FUTURE",
        objective: "Understand collaborative robots and how they differ from caged industrial robots.",
        simple: "Cobots are robots designed to work safely near people, moving slower and stopping on contact.",
        technical: "Cobots use force/torque limiting and sensing to share space with humans without full safety cages, whereas conventional industrial robots are fast, strong and typically fenced off.",
        example: "A cobot hands screws to a human assembler and pauses instantly if the person's hand gets too close.",
        ngx: "Human-in-the-Loop pairs naturally with cobots: humans and machines already collaborate; NGX structures the decision moments.",
        quiz: [
          { q: "A defining feature of a cobot?", options: ["It cannot move", "Designed to work safely alongside humans", "It only works in the cloud"], answer: 1, explain: "Cobots are built for safe human collaboration." },
        ],
        takeaways: ["Cobots = collaborative, force-limited robots.", "Industrial robots = fast/strong, usually fenced."],
      },
      {
        id: "robot-tasks", title: "Robot Tasks", level: 2, tag: "MVP",
        objective: "Map a typical robot task sequence to the NGX simulation.",
        simple: "A common task is: Detect → Pick → Inspect → Place.",
        technical: "Each step can succeed automatically or raise an exception (low confidence, unknown object, wrong destination) that needs a human decision.",
        example: "Detect a component → pick it → inspect quality → place into the correct bin.",
        ngx: "This is exactly the sequence shown in NGX Live Control. When confidence drops below threshold, NGX pauses and asks the operator.",
        flow: ["Detect", "Pick", "Inspect", "Place"],
        tryIt: { label: "Open Live Control", to: "/live" },
        quiz: [
          { q: "The task sequence used in the NGX MVP is...", options: ["Boot → Login → Logout", "Detect → Pick → Inspect → Place", "Buy → Ship → Return"], answer: 1, explain: "Detect → Pick → Inspect → Place is the simulated cycle." },
        ],
        takeaways: ["Detect → Pick → Inspect → Place.", "Exceptions trigger Human-in-the-Loop.", "Visible today in Live Control (MVP)."],
      },
      {
        id: "robot-integration", title: "Robot Integration", level: 2, tag: "FUTURE",
        objective: "Learn the interfaces used to connect to real robots.",
        simple: "To control a real robot you use its API/SDK, controller and network interface — often via ROS 2 or industrial protocols.",
        technical: "Integration paths: manufacturer APIs/SDKs, robot controllers, network interfaces, ROS/ROS 2, and industrial protocols (e.g., OPC UA). Each vendor differs — hence NGX's connector approach.",
        example: "A vendor SDK exposes moveTo(x,y,z) and gripper.open(); a Robot Connector maps NGX actions to those calls.",
        ngx: "The current MVP does NOT control physical robots. A Robot Connector is a FUTURE capability.",
        quiz: [
          { q: "Why does NGX use a connector layer for robots?", options: ["To slow things down", "Because each vendor's interface differs", "To avoid using APIs"], answer: 1, explain: "Vendor interfaces differ; connectors keep NGX Core vendor-agnostic." },
        ],
        takeaways: ["APIs/SDKs, controllers, ROS 2, industrial protocols.", "Robot control = FUTURE, not in MVP."],
      },
    ],
  },
  {
    id: "p3", title: "Artificial Intelligence", level: 2, icon: "brain",
    desc: "Industrial AI, vision and confidence.",
    lessons: [
      {
        id: "industrial-ai", title: "What is Industrial AI?", level: 2, tag: "MVP",
        objective: "Understand practical AI uses on the factory floor.",
        simple: "Industrial AI helps machines recognize objects, spot defects and predict problems.",
        technical: "Common uses: object classification, defect detection, predictive maintenance, and robot perception. Models output a prediction plus a confidence value.",
        example: "A vision model classifies a part as 'Component A' with 94% confidence.",
        ngx: "NGX consumes the AI's proposal + confidence and decides whether a human should be asked.",
        quiz: [
          { q: "Which is a typical industrial AI task?", options: ["Writing poems", "Defect detection", "Playing music"], answer: 1, explain: "Defect detection is a classic industrial AI use case." },
        ],
        takeaways: ["Industrial AI: classification, defects, prediction, perception.", "Models output a prediction + confidence."],
      },
      {
        id: "computer-vision", title: "Computer Vision", level: 2, tag: "MVP",
        objective: "Follow the vision pipeline from camera to classification.",
        simple: "A camera takes an image, a model looks at it, and it outputs what it sees plus how confident it is.",
        technical: "Pipeline: Camera → Image → Model → Classification → Confidence. Lighting, angle and part variation all affect the confidence value.",
        example: "Camera → image of a part → model → 'Component B' → confidence 67%.",
        ngx: "This drives the NGX Live Control panel: the detected object, its classification and the confidence percentage.",
        flow: ["Camera", "Image", "Model", "Classification", "Confidence"],
        quiz: [
          { q: "The correct vision pipeline order is...", options: ["Model → Camera → Image", "Camera → Image → Model → Classification → Confidence", "Confidence → Camera → Image"], answer: 1, explain: "Camera → Image → Model → Classification → Confidence." },
        ],
        takeaways: ["Vision = camera → image → model → class + confidence.", "Conditions affect confidence."],
      },
      {
        id: "ai-confidence", title: "AI Confidence", level: 2, tag: "MVP",
        objective: "Interpret confidence correctly and understand thresholds.",
        simple: "Confidence is how sure the model is — but 67% does NOT mean there is exactly a 67% chance it is right.",
        technical: "Confidence is a model score, not a calibrated probability, unless the model was calibrated. Concepts: model confidence, uncertainty, calibration, and operational thresholds that decide when a human is needed.",
        example: "AI Confidence = 67%, Operational Threshold = 75%. Since 67 < 75 → Human Intervention.",
        ngx: "NGX compares confidence to the configurable threshold in Settings. Below threshold → pause and ask a human.",
        flow: ["AI Confidence 67%", "Threshold 75%", "67 < 75", "Human Intervention"],
        tryIt: { label: "Try the Low-Confidence scenario in Live Control", to: "/live", scenario: "low_confidence" },
        quiz: [
          { q: "A 67% confidence means...", options: ["Exactly 67% chance of being correct", "A model score, not necessarily a true probability", "The model is broken"], answer: 1, explain: "Confidence is a score; it isn't a calibrated probability unless calibrated." },
          { q: "Confidence 62%, threshold 75%. NGX should...", options: ["Execute automatically", "Request human intervention", "Delete the event"], answer: 1, explain: "62 < 75 → human intervention." },
        ],
        takeaways: ["Confidence ≠ exact probability.", "Threshold decides when humans step in.", "Configurable in NGX Settings (MVP)."],
      },
    ],
  },
  {
    id: "p4", title: "Human-in-the-Loop", level: 2, icon: "user-check",
    desc: "The heart of NGX: when humans decide.",
    lessons: [
      {
        id: "hitl", title: "What is Human-in-the-Loop?", level: 2, tag: "MVP",
        objective: "Understand the HITL loop and why humans intervene.",
        simple: "Human-in-the-Loop means a person makes the call when the machine is unsure or when authorization/safety matters.",
        technical: "Loop: Machine → AI Proposal → Confidence Evaluation → NGX → Human Decision → Action → Result. Humans intervene for uncertainty, exceptions, authorization, safety review and unusual situations.",
        example: "A part scores below threshold; NGX pauses and the operator confirms or corrects the classification.",
        ngx: "This is the core NGX concept and the reason the platform exists: human judgment where autonomy needs it.",
        flow: ["Machine", "AI Proposal", "Confidence Evaluation", "NGX", "Human Decision", "Action", "Result"],
        tryIt: { label: "See Human Decisions", to: "/decisions" },
        quiz: [
          { q: "Why might a human need to intervene?", options: ["Only when the power fails", "Uncertainty, exceptions, authorization, safety", "Never"], answer: 1, explain: "HITL handles uncertainty, exceptions, authorization and safety review." },
        ],
        takeaways: ["HITL = human decides at the critical moment.", "Triggers: uncertainty, exceptions, authorization, safety."],
      },
      {
        id: "ngx-decision-model", title: "The NGX Decision Model", level: 2, tag: "MVP",
        objective: "Master the four NGX decisions.",
        simple: "Operators can Approve, Correct, Reject or Stop.",
        technical: "APPROVE: accept the AI proposal. CORRECT: change the classification/destination. REJECT: refuse the proposed action. STOP: halt the simulated process (safety-critical, requires confirmation).",
        example: "AI proposes Component A; operator selects CORRECT → Component B; the simulation follows the corrected instruction.",
        ngx: "These are the exact actions in the NGX decision panel, each stored as a structured, auditable record.",
        quiz: [
          { q: "Which action changes the AI's proposal?", options: ["APPROVE", "CORRECT", "REJECT"], answer: 1, explain: "CORRECT changes the classification/destination." },
          { q: "Which action is safety-critical and needs confirmation?", options: ["STOP", "APPROVE", "CORRECT"], answer: 0, explain: "STOP halts the process and requires confirmation." },
        ],
        takeaways: ["Approve, Correct, Reject, Stop.", "Every decision is persisted for audit."],
      },
      {
        id: "ai-confidence-exercise", title: "Interactive Exercise: AI Confidence", level: 2, tag: "MVP",
        objective: "Apply the threshold rule and try it live.",
        simple: "Given a confidence and a threshold, decide what NGX should do.",
        technical: "Scenario — AI Classification: Component B, Confidence: 67%, Threshold: 75%. Because 67 < 75, NGX should request human intervention.",
        example: "This mirrors the real Low-Confidence scenario in the Demo Control Panel.",
        ngx: "Use 'Try Scenario in Live Control' to trigger the actual simulation engine — no duplicate logic.",
        tryIt: { label: "Try Scenario in Live Control", to: "/live", scenario: "low_confidence" },
        quiz: [
          { q: "AI Component B @ 67%, threshold 75%. What should NGX do?", options: ["A. Execute automatically", "B. Request human intervention", "C. Ignore the event"], answer: 1, explain: "67 < 75 → request human intervention." },
        ],
        takeaways: ["Below threshold → human intervention.", "The exercise reuses the real engine (MVP)."],
      },
    ],
  },
  {
    id: "p5", title: "NGX Architecture", level: 3, icon: "network",
    desc: "How the MVP is actually built.",
    lessons: [
      {
        id: "ngx-architecture", title: "NGX Architecture Overview", level: 3, tag: "MVP",
        objective: "See the real end-to-end architecture of the MVP.",
        simple: "The operator uses the Control Station (frontend), which talks to FastAPI (NGX Core), which runs the Simulation Engine and stores everything in MongoDB.",
        technical: "Human Operator → NGX Human Control Station → Frontend → FastAPI / NGX Core → Simulation Engine → MongoDB. WebSockets push real-time events back to the frontend.",
        example: "A low-confidence event flows up to the operator and the decision flows back down to the engine and database.",
        ngx: "This is the actual stack you are using right now.",
        flow: ["Human Operator", "Human Control Station (Frontend)", "FastAPI / NGX Core", "Simulation Engine", "MongoDB"],
        quiz: [
          { q: "Which component stores the audit data?", options: ["The browser", "MongoDB", "The camera"], answer: 1, explain: "MongoDB persists events, tasks and decisions." },
        ],
        takeaways: ["Frontend → FastAPI/NGX Core → Engine → MongoDB.", "WebSockets deliver real-time events."],
      },
      {
        id: "frontend", title: "What is a Frontend?", level: 3, tag: "MVP",
        objective: "Understand the role of the user interface.",
        simple: "The frontend is what you see and click — the Human Control Station interface.",
        technical: "Built in React, it renders live state, sends operator actions to the API, and updates instantly from WebSocket messages.",
        example: "Live Control, Tasks, Human Decisions, Analytics and Event Log are all frontend screens.",
        ngx: "Every NGX screen you navigate is the frontend of the platform.",
        quiz: [
          { q: "The Human Control Station UI is the...", options: ["Backend", "Frontend", "Database"], answer: 1, explain: "The interface you interact with is the frontend." },
        ],
        takeaways: ["Frontend = the interface layer.", "React renders state + sends actions."],
      },
      {
        id: "backend", title: "What is a Backend? (FastAPI)", level: 3, tag: "MVP",
        objective: "Understand the server, API and business logic.",
        simple: "The backend is the brain on the server. NGX uses FastAPI to handle requests and run the logic.",
        technical: "Human Control Station → API → FastAPI → Business Logic → Database. FastAPI exposes the /api endpoints the frontend calls.",
        example: "Submitting a decision calls POST /api/sim/decision; FastAPI records it and updates the simulation.",
        ngx: "FastAPI is the NGX Core in the MVP: it evaluates confidence, manages scenarios and persists data.",
        flow: ["Human Control Station", "API", "FastAPI", "Business Logic", "Database"],
        quiz: [
          { q: "In the MVP, NGX Core is implemented with...", options: ["FastAPI", "Photoshop", "A spreadsheet"], answer: 0, explain: "FastAPI provides the backend/NGX Core logic." },
        ],
        takeaways: ["Backend = server logic + API.", "FastAPI powers NGX Core in the MVP."],
      },
      {
        id: "database", title: "The Database (MongoDB)", level: 3, tag: "MVP",
        objective: "Understand why NGX persists data and what it stores.",
        simple: "MongoDB is where NGX remembers everything: events, tasks, decisions, operators and sessions.",
        technical: "Persistence enables an auditable history. Each human decision is stored as a structured record.",
        example: "Event NGX-000011 · AI: Component B · Confidence 67% · Human: Component A · Decision: CORRECT · Operator: Operator 01.",
        ngx: "This is why Analytics and Human Decisions stay consistent and survive a refresh — one source of truth.",
        quiz: [
          { q: "Why does NGX store decisions?", options: ["To slow the app", "For an auditable history / traceability", "It doesn't"], answer: 1, explain: "Persistence provides audit and traceability." },
        ],
        takeaways: ["MongoDB stores events, tasks, decisions, operators, sessions.", "Persistence = auditability."],
      },
      {
        id: "websockets", title: "WebSockets vs Polling", level: 3, tag: "MVP",
        objective: "Understand real-time communication in NGX.",
        simple: "Polling keeps asking 'anything new?'. A WebSocket keeps an open line so the server can push instantly.",
        technical: "Simulator → FastAPI → WebSocket → Human Control Station. Real-time push means the decision modal and Event Log update with no refresh.",
        example: "Trigger a scenario and the alert appears in ~0.4s via WebSocket, not on the next poll.",
        ngx: "NGX uses WebSockets so operators react immediately to events.",
        flow: ["Simulator", "FastAPI", "WebSocket", "Human Control Station"],
        quiz: [
          { q: "The main benefit of WebSockets over polling?", options: ["Uses more battery", "Instant server push, no constant asking", "Needs no network"], answer: 1, explain: "WebSockets push in real time instead of repeated polling." },
        ],
        takeaways: ["Polling = repeatedly ask. WebSocket = server pushes.", "NGX pushes events in real time (MVP)."],
      },
    ],
  },
  {
    id: "p6", title: "Industrial Communication", level: 4, icon: "radio",
    desc: "REST, MQTT, OPC UA and ROS 2.",
    lessons: [
      {
        id: "rest-api", title: "REST API", level: 4, tag: "MVP",
        objective: "Understand basic request/response communication.",
        simple: "A REST API is a request/response system: you ask (request), the server answers (response).",
        technical: "Client sends an HTTP request (GET/POST) to an endpoint; the server returns data (usually JSON). Stateless and simple.",
        example: "GET /api/analytics returns the current metrics as JSON.",
        ngx: "The NGX frontend calls REST endpoints for state, tasks, analytics and decisions.",
        quiz: [
          { q: "REST communication is best described as...", options: ["Publish/subscribe", "Request/response", "Broadcast only"], answer: 1, explain: "REST is a request/response model." },
        ],
        takeaways: ["REST = request/response over HTTP.", "NGX uses REST for most data (MVP)."],
      },
      {
        id: "mqtt", title: "MQTT (Publish/Subscribe)", level: 4, tag: "FUTURE",
        objective: "Understand lightweight pub/sub messaging for machines.",
        simple: "MQTT lets machines publish messages to topics; anyone subscribed gets them. Great for many small sensors.",
        technical: "A broker routes messages: publishers send to topics, subscribers receive. Lightweight and ideal for constrained/IoT devices.",
        example: "Machine → MQTT Broker → NGX. A sensor publishes 'temp=80' and NGX (subscriber) reacts.",
        ngx: "An MQTT Connector is a FUTURE integration; not in the current MVP.",
        flow: ["Machine", "MQTT Broker", "NGX"],
        quiz: [
          { q: "MQTT uses which pattern?", options: ["Request/response", "Publish/subscribe via a broker", "File transfer"], answer: 1, explain: "MQTT is publish/subscribe through a broker." },
        ],
        takeaways: ["MQTT = pub/sub via a broker.", "Great for IoT/sensors.", "FUTURE NGX connector."],
      },
      {
        id: "opcua", title: "OPC UA", level: 4, tag: "FUTURE",
        objective: "Understand the common industrial interoperability standard.",
        simple: "OPC UA is a standard way for industrial machines and PLCs to share data securely across vendors.",
        technical: "It provides a structured, secure, platform-independent information model widely used in factories for machine-to-system communication.",
        example: "PLC / Machine → OPC UA → NGX Connector exposes machine variables to NGX.",
        ngx: "An OPC UA Connector is a FUTURE integration; it is not implemented in the MVP.",
        flow: ["PLC / Machine", "OPC UA", "NGX Connector"],
        quiz: [
          { q: "OPC UA is popular in industry because it is...", options: ["A game engine", "A secure, vendor-neutral machine data standard", "A video codec"], answer: 1, explain: "OPC UA standardizes secure industrial data exchange." },
        ],
        takeaways: ["OPC UA = standardized industrial data exchange.", "FUTURE NGX connector."],
      },
      {
        id: "ros2", title: "ROS 2", level: 4, tag: "FUTURE",
        objective: "Understand the robotics middleware in an NGX context.",
        simple: "ROS 2 is a framework robots use internally to move data between their parts (sensors, planners, motors).",
        technical: "ROS 2 provides nodes, topics and services for real-time robotics with a pub/sub backbone (DDS). Many robots expose ROS 2 interfaces.",
        example: "Robot → ROS 2 → NGX Robot Connector bridges robot topics to NGX Core.",
        ngx: "A ROS 2-based Robot Connector is a FUTURE capability, not in the MVP.",
        flow: ["Robot", "ROS 2", "NGX Robot Connector"],
        quiz: [
          { q: "ROS 2 is mainly used for...", options: ["Accounting", "Robotics middleware/communication", "Web hosting"], answer: 1, explain: "ROS 2 is robotics middleware." },
        ],
        takeaways: ["ROS 2 = robotics middleware (nodes/topics).", "FUTURE NGX robot integration."],
      },
    ],
  },
  {
    id: "p7", title: "NGX Connectors", level: 4, icon: "plug",
    desc: "One NGX Core. Multiple connectors.",
    lessons: [
      {
        id: "connectors", title: "The Connector Concept", level: 4, tag: "MVP",
        objective: "Understand why NGX Core stays vendor-independent.",
        simple: "NGX Core never talks to a specific brand directly. Connectors translate between NGX and each system.",
        technical: "NGX Core → Connector Layer → {Robot, PLC, Vision, Machine}. Swapping equipment means swapping a connector, not rebuilding NGX.",
        example: "Today: Simulation Connector (active). Future: Robot, PLC, Vision, Machine connectors.",
        ngx: "This is visible on the Systems page: Simulation Cell 01 is CONNECTED; the rest are AVAILABLE ARCHITECTURE / NOT CONNECTED.",
        flow: ["NGX Core", "Connector Layer", "Robot / PLC / Vision"],
        tryIt: { label: "Open Systems", to: "/systems" },
        quiz: [
          { q: "Why use connectors?", options: ["To lock into one vendor", "To keep NGX Core vendor-agnostic", "To remove the database"], answer: 1, explain: "Connectors keep the Core independent of any manufacturer." },
          { q: "In the MVP, which connector is active?", options: ["Robot Connector", "Simulation Connector", "OPC UA Connector"], answer: 1, explain: "Only the Simulation Connector is active in the MVP." },
        ],
        takeaways: ["One Core, many connectors.", "Swap equipment = swap connector.", "Only Simulation Connector is active (MVP)."],
      },
    ],
  },
  {
    id: "p8", title: "Industrial Safety", level: 4, icon: "shield-alert",
    desc: "Operational STOP vs certified safety.",
    lessons: [
      {
        id: "safety", title: "Industrial Safety Basics", level: 4, tag: "CONCEPT",
        objective: "Understand safety systems and NGX's boundary.",
        simple: "Real safety uses certified hardware: emergency stops, safety PLCs and safety circuits — not just software.",
        technical: "Concepts: emergency stop, safety PLC, safety circuits, risk assessment, certified safety systems. These are engineered and certified to standards.",
        example: "A physical E-stop cuts motor power through a certified safety circuit, independent of any software.",
        ngx: "Crucial: NGX operational STOP pauses the simulated process. It is NOT a substitute for a physical Emergency Stop or certified safety systems.",
        quiz: [
          { q: "The NGX STOP is...", options: ["A replacement for a certified E-stop", "An operational pause, NOT a certified safety system", "A physical circuit"], answer: 1, explain: "NGX software STOP never replaces certified physical safety systems." },
        ],
        takeaways: ["Certified safety = hardware (E-stop, safety PLC).", "NGX STOP = operational pause only.", "Never present NGX as a safety-system replacement."],
      },
    ],
  },
  {
    id: "p9", title: "Cybersecurity", level: 4, icon: "lock",
    desc: "Securing NGX in real deployments.",
    lessons: [
      {
        id: "cybersecurity", title: "Industrial Cybersecurity", level: 4, tag: "CONCEPT",
        objective: "Understand the security responsibilities of connecting NGX to real systems.",
        simple: "Connecting to real machines means protecting who can access and command them.",
        technical: "Key controls: authentication, authorization, network segmentation, encryption, API security, audit logs, and least privilege.",
        example: "Only an authenticated operator with the right role may issue commands; all actions are logged.",
        ngx: "The MVP already uses operator authentication and an audit trail — a foundation for stricter production security.",
        quiz: [
          { q: "'Least privilege' means...", options: ["Everyone is admin", "Give only the access strictly needed", "No passwords"], answer: 1, explain: "Least privilege grants only necessary access." },
        ],
        takeaways: ["AuthN/AuthZ, segmentation, encryption, audit, least privilege.", "Real integration = real security duty."],
      },
    ],
  },
  {
    id: "p10", title: "From Simulation to Real Machine", level: 5, icon: "route",
    desc: "The evolution path.",
    lessons: [
      {
        id: "sim-to-real", title: "From Simulation to Real Machine", level: 5, tag: "FUTURE",
        objective: "Understand what changes and what stays when moving to real hardware.",
        simple: "The Human Control Station and decision model stay; the Simulation Connector is replaced by a real equipment connector.",
        technical: "Path: NGX Simulation MVP → Simulation Connector → First Technology Partner → Real Connector → Robot/Machine/PLC → Controlled Test → Industrial Pilot.",
        example: "Same UI and audit trail; only the connector behind NGX Core changes.",
        ngx: "Stays: Human Control Station, NGX decision model, event model, audit history, core UI. Changes: Simulation Connector → Real Equipment Connector.",
        flow: ["NGX Simulation MVP", "Simulation Connector", "Technology Partner", "Real Connector", "Robot / Machine / PLC", "Controlled Test", "Industrial Pilot"],
        quiz: [
          { q: "What mainly changes when going to real hardware?", options: ["The whole UI is rebuilt", "The connector is swapped for a real one", "MongoDB is removed"], answer: 1, explain: "The connector changes; the Control Station and decision model remain." },
        ],
        takeaways: ["Keep: UI, decision model, event model, audit.", "Change: Simulation Connector → Real Connector."],
      },
    ],
  },
  {
    id: "p11", title: "Industrial Pilot", level: 5, icon: "clipboard-check",
    desc: "Validating NGX on a real use case.",
    lessons: [
      {
        id: "industrial-pilot", title: "What is an Industrial Pilot?", level: 5, tag: "FUTURE",
        objective: "Know what a pilot involves.",
        simple: "A pilot is a small, real, controlled deployment to prove NGX on one concrete use case.",
        technical: "Covers: use-case definition, integration scope, technical requirements, acceptance criteria, performance metrics, operator feedback, safety, cybersecurity and documentation.",
        example: "Pilot: supervise a single pick-and-place cell for two weeks with defined acceptance criteria.",
        ngx: "The pilot reuses the NGX Control Station and audit trail with a real connector in place of the simulator.",
        quiz: [
          { q: "A pilot must define...", options: ["Nothing in advance", "Clear acceptance criteria and scope", "Only the color scheme"], answer: 1, explain: "Scope and acceptance criteria are essential to a pilot." },
        ],
        takeaways: ["Pilot = small, real, controlled validation.", "Define scope, acceptance, safety, security, docs."],
      },
    ],
  },
  {
    id: "p12", title: "Explain NGX to an Engineer", level: 5, icon: "messages-square",
    desc: "Communicate and qualify a partner.",
    lessons: [
      {
        id: "explain-to-engineer", title: "Explaining & Qualifying", level: 5, tag: "MVP",
        objective: "Pitch NGX at three depths and ask the right partner questions.",
        simple: "Be able to explain NGX in 30 seconds, 2 minutes, and 5 minutes — and know what to ask a partner.",
        technical: "30s: NGX is a vendor-agnostic Human-in-the-Loop supervision layer for intelligent industrial systems. 2min: Control Station → NGX Core → Connector Layer → equipment; humans decide when AI is unsure or authorization/safety is needed; everything is audited. 5min: walk the connector concept and how a robot would integrate via API/SDK or ROS 2.",
        example: "Partner questions: What interfaces exist? Is there an API/SDK? OPC UA? MQTT? ROS 2? What data is accessible? What commands can run? What safety restrictions exist? Is simulation available? What authentication is required?",
        ngx: "This module turns your NGX knowledge into a conversation that can start a real integration.",
        quiz: [
          { q: "A strong 30-second definition of NGX is...", options: ["A robot brand", "A vendor-agnostic Human-in-the-Loop supervision layer", "A spreadsheet macro"], answer: 1, explain: "NGX is a vendor-agnostic HITL supervision/decision layer." },
          { q: "A key question to ask a partner is...", options: ["What's your favorite color?", "Is there an API/SDK and does it support OPC UA/MQTT/ROS 2?", "Do you like NGX?"], answer: 1, explain: "Interface/API questions determine integration feasibility." },
        ],
        takeaways: ["Pitch at 30s / 2min / 5min.", "Qualify via interface, data, commands, safety, auth questions."],
      },
    ],
  },
];

export const ALL_LESSONS = PATHS.flatMap((p) =>
  p.lessons.map((l) => ({ ...l, pathId: p.id, pathTitle: p.title }))
);
export const TOTAL_LESSONS = ALL_LESSONS.length;

export function findLesson(id) {
  const idx = ALL_LESSONS.findIndex((l) => l.id === id);
  return { lesson: ALL_LESSONS[idx], prev: ALL_LESSONS[idx - 1], next: ALL_LESSONS[idx + 1] };
}
